/**
 * LifecycleState — re-subscribes stalled Firestore listeners and re-warms the
 * session when the app returns from background / regains connectivity
 * (ELARA-695).
 *
 * Elara's UX is entirely listener-driven, but mobile OSes suspend sockets and
 * timers while backgrounded: `onSnapshot` streams stall silently and the
 * periodic id-token refresh stops firing, so the app is stale on resume. This
 * `Monitorable` plugs the platform {@link LifecyclePort} into the existing
 * `startMonitors`/`stopMonitors` lifecycle and, on each resume, re-establishes
 * active listeners and re-fires the token-refresh + warmup.
 */

import type { LifecyclePort } from '#lib/lifecycle-port.js';
import type { SessionState } from './SessionState.js';

import { resubscribeActiveListeners } from '#firebase/listener-registry.js';
import { createState } from '#lib/signals-utils.js';

/**
 * Minimum gap between resume handlings. Coalesces duplicate resume signals — web
 * fires `visibilitychange` and `online` together; React Native fires `AppState`
 * `active` alongside a NetInfo reconnect.
 */
const RESUME_DEBOUNCE_MS = 1_000;

export function createLifecycleState(lifecyclePort: LifecyclePort, sessionState: SessionState) {
  const state = createState({
    /** Timestamp of the last handled resume, for debounce coalescing. */
    lastResumeAt: 0,

    /** Cleanup for the lifecycle-port subscription; presence means "running". */
    stopMonitors: undefined as (() => void) | undefined,

    /**
     * Handles an "app resumed" signal: re-subscribe stalled listeners and
     * re-warm auth/backend. Idempotent and debounced against duplicate signals.
     */
    handleResume(): void {
      const now = Date.now();
      if (now - this.lastResumeAt < RESUME_DEBOUNCE_MS) return;
      this.lastResumeAt = now;

      console.debug('[LifecycleState] App resumed — re-subscribing listeners and re-warming session');
      // Tear down and re-establish active Firestore listeners. Mobile suspends
      // sockets on background, so onSnapshot can stall silently without erroring.
      resubscribeActiveListeners();
      // Re-fire token refresh + warmup (cold-resume seam). Non-fatal.
      void sessionState.handleResume();
    },

    /**
     * Subscribes to resume events via the injected {@link LifecyclePort}.
     * Idempotent: repeated calls return the existing cleanup function.
     */
    startMonitors(): () => void {
      if (this.stopMonitors) return this.stopMonitors;

      const unsubscribe = lifecyclePort.onResume(() => this.handleResume());

      this.stopMonitors = () => {
        unsubscribe();
        this.stopMonitors = undefined;
      };
      return this.stopMonitors;
    },
  });

  return state;
}

export type LifecycleState = ReturnType<typeof createLifecycleState>;
